'use client';

import { useContext } from 'react';
import { useTranslations } from 'next-intl';

import { useProductOffersData } from '@workspace/lib/hooks/offers/use-product-offers-data';

import { pageLayoutSchema } from '@/business-logic/schemas/page-layout/page-layout-schema';
import { APP_ROUTES } from '@/constants/application-routes';
import { usePathname } from '@/i18n/navigation';
import { ProgressBarContext } from '@/providers/progress-bar-provider';

export function usePageLayoutContainer() {
  const pathname = usePathname();
  const { getTypeOfCredit } = useProductOffersData();
  const { progressBarSchema, showProgressBarUrlsList } =
    useContext(ProgressBarContext) ?? {};

  const t = useTranslations();

  const isProgressBarShown =
    showProgressBarUrlsList?.includes(pathname) ?? false;

  // Note: define whether to show gradient on the offer page or not
  const isProvisionalOfferPage = pathname.includes(
    APP_ROUTES.PATHS.DIGITAL_OFFER,
  );
  const isFinalOfferPage = pathname.includes(APP_ROUTES.PATHS.FINAL_OFFER);
  const isDeclinedOfferPage = pathname.includes(APP_ROUTES.PATHS.DECLINED);
  const isBrokenJourneyPage = pathname.includes(
    APP_ROUTES.PATHS.BROKEN_JOURNEY,
  );
  const isOfferPage =
    isProvisionalOfferPage || isFinalOfferPage || isDeclinedOfferPage;
  const typeOfCredit = isProvisionalOfferPage ? getTypeOfCredit() : null;
  const showProvisionalOfferGradient =
    pathname.includes(APP_ROUTES.PATHS.DIGITAL_OFFER) &&
    typeOfCredit === 'LoanCard';

  const isOfferPageGradientShown =
    showProvisionalOfferGradient || isFinalOfferPage;

  const isThankYouPage = pathname.includes(APP_ROUTES.PATHS.THANK_YOU);
  const isDashboardPage =
    pathname === `/${APP_ROUTES.PATHS.DASHBOARD}` ||
    pathname.startsWith(`/${APP_ROUTES.PATHS.DASHBOARD}/`);

  // Header logout is shown wherever the customer is signed in outside the
  // application form: the dashboard and the (SSO-protected) broken journey,
  // matching PL. Use an exact match for broken journey: isBrokenJourneyPage is
  // a substring check, and off-map paths like '/broken-journey-foo' are public.
  const isBrokenJourneyRoute =
    pathname === `/${APP_ROUTES.PATHS.BROKEN_JOURNEY}`;
  const logoutButtonText =
    isDashboardPage || isBrokenJourneyRoute
      ? pageLayoutSchema.navbar.logoutButtonText &&
        t(pageLayoutSchema.navbar.logoutButtonText)
      : null;

  const phoneNumber =
    pageLayoutSchema.navbar.phoneNumber &&
    t(pageLayoutSchema.navbar.phoneNumber);

  return {
    progressBarSchema,
    isProgressBarShown,
    isOfferPageGradientShown,
    isOfferPage,
    isThankYouPage,
    isDashboardPage,
    isBrokenJourneyPage,
    logoutButtonText,
    phoneNumber,
  };
}
